import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { adminApi, downloadCsv } from "./adminApi";

const CLASSES = ["Class 11", "Class 12", "Dropper"];
const EXAMS = ["NEET", "JEE Main", "JEE Advanced", "Other"];

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(null);
  const [filters, setFilters] = useState({ from_date: "", to_date: "", current_class: "", target_exam: "", topic_id: "" });
  const update = (key, value) => setFilters((c) => ({ ...c, [key]: value }));

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const [{ data }, { data: lb }] = await Promise.all([
      adminApi.get("/admin/test/analytics/topics", { params }),
      adminApi.get("/admin/test/leaderboard"),
    ]);
    setData(data);
    setWeek(lb.top);
  }, [filters]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const exportCsv = () => downloadCsv("attempts", Object.fromEntries(Object.entries(filters).filter(([, v]) => v)));

  return (
    <div data-testid="admin-analytics">
      <h4 style={{ marginBottom: 4 }}>Topic-wise analytics</h4>
      <span className="lead-sub">Attempts, scores and accuracy per Physics topic. Filter by date, class, exam or topic.</span>
      <div className="admin-filters" style={{ marginTop: 14 }}>
        <label className="lead-sub">From <input type="date" value={filters.from_date} onChange={(e) => update("from_date", e.target.value)} data-testid="an-from" /></label>
        <label className="lead-sub">To <input type="date" value={filters.to_date} onChange={(e) => update("to_date", e.target.value)} data-testid="an-to" /></label>
        <select value={filters.current_class} onChange={(e) => update("current_class", e.target.value)} data-testid="an-class"><option value="">All classes</option>{CLASSES.map((c) => <option key={c}>{c}</option>)}</select>
        <select value={filters.target_exam} onChange={(e) => update("target_exam", e.target.value)} data-testid="an-exam"><option value="">All exams</option>{EXAMS.map((x) => <option key={x}>{x}</option>)}</select>
        <select value={filters.topic_id} onChange={(e) => update("topic_id", e.target.value)} data-testid="an-topic"><option value="">All topics</option>{data?.all_topics.map((t) => <option key={t.topic_id} value={t.topic_id}>{t.name}</option>)}</select>
        <button className="admin-btn" onClick={exportCsv} data-testid="an-export"><Download size={14} /> Export results CSV</button>
      </div>
      {data && <div className="stat-cards" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))" }}>
        <div className="stat-card"><b style={{ fontSize: 18 }}>{data.most_attempted || "—"}</b><span>MOST ATTEMPTED TOPIC</span></div>
        <div className="stat-card"><b style={{ fontSize: 18 }}>{data.lowest_avg_score || "—"}</b><span>LOWEST AVG SCORE (NEEDS ATTENTION)</span></div>
      </div>}
      {week && <div className="admin-card" data-testid="an-weekly" style={{ marginBottom: 18 }}>
        <h4 style={{ marginBottom: 4 }}>This week&rsquo;s challenge — top scores</h4>
        <span className="lead-sub">Best score per student over the last 7 days. Announce the #1 on WhatsApp/Instagram to fuel the challenge.</span>
        {week.length === 0 ? <div className="admin-empty" data-testid="an-weekly-empty">No test scores in the last 7 days yet.</div> : (
          <div className="table-scroll"><table className="leads-table" data-testid="an-weekly-table">
            <thead><tr><th>#</th><th>STUDENT</th><th>TOPIC</th><th>SCORE</th><th>DATE</th></tr></thead>
            <tbody>{week.map((w) => (
              <tr key={w.student_id} data-testid={`an-weekly-row-${w.rank}`}>
                <td><b style={{ color: w.rank === 1 ? "#f77f00" : "inherit" }}>{w.rank}</b></td>
                <td><div className="lead-name">{w.name}</div><div className="lead-sub">{w.whatsapp} · {w.current_class} · {w.target_exam}</div></td>
                <td>{w.topic_name}</td>
                <td><b>{w.score}/{w.max_score}</b><div className="lead-sub">{w.correct}/5 correct · {w.accuracy}%</div></td>
                <td>{w.submitted_at ? new Date(w.submitted_at * 1000).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>}
      {data && (data.topics.length === 0 ? <div className="admin-empty" data-testid="an-empty">No attempts match these filters yet.</div> : (
        <div className="table-scroll"><table className="leads-table" data-testid="an-table">
          <thead><tr><th>TOPIC</th><th>ATTEMPTS</th><th>STUDENTS</th><th>AVG SCORE</th><th>AVG ACCURACY</th></tr></thead>
          <tbody>{data.topics.map((t, i) => (
            <tr key={t.topic_id || "legacy"} data-testid={`an-row-${i}`}>
              <td><div className="lead-name">{t.topic_name}</div></td>
              <td data-testid={`an-attempts-${i}`}>{t.attempts}</td>
              <td>{t.students}</td>
              <td>{t.avg_score}/20</td>
              <td>{t.avg_accuracy}%</td>
            </tr>
          ))}</tbody>
        </table></div>
      ))}
    </div>
  );
}
